import { isSameDay } from 'date-fns';
import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { PageHeader } from 'components/PageHeader';
import { SeparatorHorizontal } from 'components/SeparatorHorizontal';
import { useEsc } from 'hooks/useEsc';
import { UI_DATE_FORMAT_LONG_WITH_DAY } from 'lib/constants';
import { formatDate, isoDateStringToDate } from 'lib/riskbase';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NAVIGATE_MODAL_BACK } from 'store/actions';
import { doEngageFlatScheduledVisitAvailabilitySave } from 'store/remote/engageActions';
import { selectEngageFlat } from 'store/remote/engageSelectors';
import { EngageFlatScheduledVisit } from 'types/Engage';
import {
  formatScheduledVisitSlotTimeRange,
  getScheduledVisitDateRangeText,
  getScheduledVisitSlotDayGroups,
} from './scheduledVisitUtils';

type ScheduledVisitAvailability = {
  isUnavailable: EngageFlatScheduledVisit['isUnavailable'];
  slots: Pick<
    EngageFlatScheduledVisit['slots'][number],
    'id' | 'hasResponse'
  >[];
};

const getScheduledVisitAvailability = (
  visit: EngageFlatScheduledVisit,
): ScheduledVisitAvailability => ({
  isUnavailable: visit.isUnavailable,
  slots: visit.slots.map((slot) => ({
    id: slot.id,
    hasResponse: slot.hasResponse,
  })),
});

const getSlotResponsesById = (
  slots: ScheduledVisitAvailability['slots'],
): Record<string, boolean | undefined> =>
  Object.fromEntries(slots.map((slot) => [slot.id, slot.hasResponse]));

const AvailabilitySlots: React.FC<{
  visit: EngageFlatScheduledVisit;
  availability: ScheduledVisitAvailability;
  onToggleUnavailable: () => void;
  onToggleSlot: (slotId: string) => void;
}> = ({ visit, availability, onToggleUnavailable, onToggleSlot }) => {
  const slotsById = React.useMemo(
    () => getSlotResponsesById(availability.slots),
    [availability.slots],
  );
  const dayGroups = React.useMemo(
    () =>
      getScheduledVisitSlotDayGroups(visit.slots).map((dayGroup) => ({
        ...dayGroup,
        heading: formatDate(dayGroup.slotDate, UI_DATE_FORMAT_LONG_WITH_DAY),
      })),
    [visit.slots],
  );
  return (
    <>
      <Card>
        <CardRow
          label="Resident Unavailable"
          checkedValue={availability.isUnavailable}
          onPress={onToggleUnavailable}
          onIconPress={onToggleUnavailable}
        />
      </Card>
      {dayGroups.map((day) => (
        <Card key={day.key} label={day.heading}>
          {day.slots.map((slot) => (
            <CardRow
              key={slot.id}
              label={formatScheduledVisitSlotTimeRange(
                slot.startDateTime,
                slot.endDateTime,
              )}
              checkedValue={slotsById[slot.id] ?? slot.hasResponse}
              disabled={availability.isUnavailable}
              onPress={() => onToggleSlot(slot.id)}
              onIconPress={
                availability.isUnavailable
                  ? undefined
                  : () => onToggleSlot(slot.id)
              }
            />
          ))}
        </Card>
      ))}
    </>
  );
};

export const EngageFlatScheduledVisitDataModal: React.FC<
  ModalScreenProps<ModalScreens.EngageFlatScheduledVisitData>
> = ({ route }) => {
  const dispatch = useDispatch();
  const { key, flatName, scheduledVisit: routeScheduledVisit } = route.params;
  const flatData = useSelector((state) => selectEngageFlat(state, key));
  const scheduledVisit =
    flatData?.scheduledVisits.find(
      (visit) => visit.id === routeScheduledVisit.id,
    ) ?? routeScheduledVisit;
  const scheduledVisitStartDate = isoDateStringToDate(
    scheduledVisit.startDateTime,
  );
  const scheduledVisitEndDate = isoDateStringToDate(scheduledVisit.endDateTime);
  const scheduledVisitSpansMultipleDates =
    !!scheduledVisitStartDate &&
    !!scheduledVisitEndDate &&
    !isSameDay(scheduledVisitStartDate, scheduledVisitEndDate);

  const [availability, setAvailability] = React.useState(() =>
    getScheduledVisitAvailability(scheduledVisit),
  );

  React.useEffect(() => {
    setAvailability(getScheduledVisitAvailability(scheduledVisit));
  }, [scheduledVisit]);

  const handleClose = React.useCallback(() => {
    dispatch(NAVIGATE_MODAL_BACK());
  }, [dispatch]);

  const handleToggleUnavailable = React.useCallback(() => {
    setAvailability((current) => {
      const isUnavailable = !current.isUnavailable;
      return {
        isUnavailable,
        slots: current.slots.map((slot) => ({
          ...slot,
          hasResponse: isUnavailable ? false : slot.hasResponse,
        })),
      };
    });
  }, []);

  const handleToggleSlot = React.useCallback((slotId: string) => {
    setAvailability((current) => {
      if (current.isUnavailable) {
        return current;
      }
      return {
        ...current,
        slots: current.slots.map((slot) =>
          slot.id === slotId
            ? { ...slot, hasResponse: !slot.hasResponse }
            : slot,
        ),
      };
    });
  }, []);

  const handleSave = React.useCallback(() => {
    if (!flatData) {
      return;
    }
    dispatch(
      doEngageFlatScheduledVisitAvailabilitySave({
        key,
        propertyId: flatData.property.id,
        flatId: flatData.flatDetails.id,
        scheduledVisitId: scheduledVisit.id,
        ...availability,
      }),
    );
  }, [availability, dispatch, flatData, key, scheduledVisit.id]);

  useEsc(handleClose);

  return (
    <Page
      scrollView
      header={
        <PageHeader
          title="Scheduled Visit"
          subtitle={flatName}
          onPressCancelButton={handleClose}
          onPressSaveButton={flatData ? handleSave : null}
          saveButtonText="Save"
        />
      }
    >
      <Card>
        <CardRow
          label={scheduledVisitSpansMultipleDates ? 'Dates Between' : 'Date'}
          value={getScheduledVisitDateRangeText(scheduledVisit)}
          labelAlignTop
        />
      </Card>
      <SeparatorHorizontal />
      <AvailabilitySlots
        visit={scheduledVisit}
        availability={availability}
        onToggleUnavailable={handleToggleUnavailable}
        onToggleSlot={handleToggleSlot}
      />
    </Page>
  );
};
